import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Badge } from "@chakra-ui/react";
import { Eye, EllipsisVertical, Pencil } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPaymentProofsAction,
  updatePaymentProofStatusAction,
} from "../../../state/actions/paymentProofActions";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ViewPaymentProofDrawer from "./components/ViewPaymentProofDrawer";
import ReviewPaymentProofDrawer from "./components/ReviewPaymentProofDrawer";
import { useCustomNotification } from "components/Toasts/OpenNotification";

const statusColorScheme = {
  Verified: "green",
  Pending: "yellow",
  Suspended: "red",
  Rejected: "red",
  Deactivated: "gray",
};

const PaymentProofsTable = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();
  const { proofs, loading } = useSelector((state) => state.proofs);

  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isReviewDrawerOpen, setReviewDrawerOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);

  useEffect(() => {
    dispatch(fetchPaymentProofsAction());
  }, [dispatch]);

  // Open/close drawers
  const openViewDrawer = (proof) => {
    setSelectedProof(proof);
    setViewDrawerOpen(true);
  };
  const closeViewDrawer = () => {
    setSelectedProof(null);
    setViewDrawerOpen(false);
  };

  const openReviewDrawer = (proof) => {
    setReviewDrawerOpen(true);
  };
const closeReviewDrawer = () => {
  setReviewDrawerOpen(false);  
  setViewDrawerOpen(false);  
  setSelectedProof(null);      
};


  const handleUpdateStatus = async (payment_id, newStatus) => {
    if (!payment_id) {
      notify({
        type: "error",
        message: "Missing Payment ID",
        description: "Cannot update status because payment_id is missing",
        duration: 5,
      });
      return;
    }



    try {
      await dispatch(updatePaymentProofStatusAction(payment_id, newStatus));
      dispatch(fetchPaymentProofsAction());
      notify({
        type: "success",
        message: "Status Updated",
        description: `Payment proof status updated to "${newStatus}"`,
        duration: 3,
      });
    } catch (error) {
      notify({
        type: "error",
        message: "Update Failed",
        description: error.message || "Failed to update payment proof status",
        duration: 5,
      });
    }
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View Proof",
      onClick: () => openViewDrawer(row.original),
    }
  ];

  // Map data to table
  const tableData = proofs.map((p) => ({
    name: `${p.user?.first_name ?? ""} ${p.user?.last_name ?? ""}`.trim() || "N/A",
    phone_number: p.user?.phone_number ?? "N/A",
    plan_name: p.plan_name ?? "N/A",
    amount: p.amount ?? 0,
    reference: p.reference ?? "N/A",
    status: p.status ?? "N/A",
    uploaded_at: p.uploaded_at,
    verified_at: p.verified_at,
    documents: p.documents ?? [],
    original: p,
    payment_id: p.payment_id,
  }));

  const columnsData = [
    { Header: "Name", accessor: "name" },
    { Header: "Phone Number", accessor: "phone_number" },
    { Header: "Plan Name", accessor: "plan_name" },
    { Header: "Amount", accessor: "amount" },
    { Header: "Reference", accessor: "reference" },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge
          colorScheme={statusColorScheme[value] || "gray"}
          borderRadius="full"
          px={2}
        >
          {value}
        </Badge>
      ),
    },
    {
      Header: "Uploaded At",
      accessor: "uploaded_at",
      Cell: ({ value }) => {
        if (!value) return "N/A";

        const date = new Date(value);
        // Shift +2 hours
        date.setHours(date.getHours() + 2);

        return date.toLocaleString();
      },
    },

    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="flex-end" mb={4} pt={{ base: "120px", md: "25px" }}>
        {loading && <Skeleton width="140px" height="40px" borderRadius="md" />}
      </Flex>

      <ReusableTable
        columnsData={columnsData}
        tableData={tableData}
        isLoading={loading}
      />

      {/* View Drawer */}
      <ReusableDrawer
        isOpen={isViewDrawerOpen}
        onClose={closeViewDrawer}
        headerContent={<h2 style={{ fontSize: "md" }}>View Payment Proof</h2>}
        bodyContent={
          selectedProof && (
            <ViewPaymentProofDrawer proof={selectedProof} onClose={closeViewDrawer} onOpenReview={openReviewDrawer} />
          )
        }
      />

      {/* Review Drawer */}
      <ReviewPaymentProofDrawer
        isOpen={isReviewDrawerOpen}
        onClose={closeReviewDrawer}
        proof={selectedProof}
        onUpdateStatus={handleUpdateStatus}
      />
    </Box>
  );
};

export default PaymentProofsTable;
